export function isLoopbackHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname === '[::1]'
  );
}

export function isPrivateLanHost(hostname: string): boolean {
  if (isLoopbackHost(hostname)) return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  return /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(hostname);
}

/** True when the API base URL points at loopback or a private LAN address. */
export function isLoopbackOrPrivateApiUrl(url: string): boolean {
  try {
    const { hostname, protocol } = new URL(url);
    if (protocol !== 'http:' && protocol !== 'https:') return false;
    return isPrivateLanHost(hostname);
  } catch {
    return false;
  }
}

export type BackendUrlContext = {
  dev: boolean;
  envApiUrl?: string;
  hostname?: string;
  apiPort?: string;
};

/**
 * Resolves the backend origin for API calls.
 * Returns '' for same-origin `/api` (Vite dev proxy or Nginx in production).
 */
export function resolveBackendUrl(ctx: BackendUrlContext): string {
  if (ctx.dev) {
    return '';
  }

  const apiPort = ctx.apiPort || '3010';
  const { hostname } = ctx;

  if (hostname !== undefined) {
    const onLoopback = isLoopbackHost(hostname);

    if (ctx.envApiUrl) {
      // Browsers block public sites from calling loopback (Private Network Access).
      if (!onLoopback && isLoopbackOrPrivateApiUrl(ctx.envApiUrl)) {
        return '';
      }
      return ctx.envApiUrl;
    }

    if (onLoopback) {
      return `http://localhost:${apiPort}`;
    }

    return '';
  }

  if (ctx.envApiUrl && !isLoopbackOrPrivateApiUrl(ctx.envApiUrl)) {
    return ctx.envApiUrl;
  }

  return '';
}

export const getBackendUrl = (): string => {
  const env = (
    import.meta as { env?: { DEV?: boolean; VITE_API_URL?: string; VITE_API_PORT?: string } }
  ).env;

  return resolveBackendUrl({
    dev: Boolean(env?.DEV),
    envApiUrl: env?.VITE_API_URL,
    hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
    apiPort: env?.VITE_API_PORT,
  });
};

export const getApiUrl = (endpoint: string): string => {
  let backendUrl = getBackendUrl().replace(/\/+$/, '');
  let cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  if (backendUrl.endsWith('/api')) {
    backendUrl = backendUrl.replace(/\/api$/, '');
  }

  if (cleanEndpoint.startsWith('/api/')) {
    cleanEndpoint = cleanEndpoint.replace(/^\/api/, '');
  }

  return `${backendUrl}/api${cleanEndpoint}`;
};
